(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.MNGiveawayMath = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function positiveNumber(value) {
    if (typeof value === 'string') {
      value = value.trim();
      if (!/^(?:\d+\.?\d*|\.\d+)$/.test(value)) return null;
      value = Number(value);
    }
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
  }

  function timestamp(value) {
    if (typeof value !== 'string') return null;
    var match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|([+-])(\d{2}):(\d{2}))$/.exec(value.trim());
    if (!match) return null;
    var year = Number(match[1]);
    var month = Number(match[2]);
    var day = Number(match[3]);
    var hour = Number(match[4]);
    var minute = Number(match[5]);
    var second = Number(match[6] || 0);
    var milliseconds = Number((match[7] || '').padEnd(3, '0'));
    var offsetHours = Number(match[10] || 0);
    var offsetMinutes = Number(match[11] || 0);
    if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59) return null;
    if (offsetHours > 14 || offsetMinutes > 59 || (offsetHours === 14 && offsetMinutes !== 0)) return null;
    var local = new Date(0);
    local.setUTCFullYear(year, month - 1, day);
    local.setUTCHours(hour, minute, second, milliseconds);
    if (local.getUTCFullYear() !== year || local.getUTCMonth() !== month - 1 || local.getUTCDate() !== day) return null;
    var offset = (offsetHours * 60 + offsetMinutes) * (match[9] === '-' ? -1 : 1);
    return local.getTime() - offset * 60000;
  }

  // Latest technical cutoff for the current 187 rules. Operators must choose an
  // earlier receipt deadline that leaves processing time before the drawing.
  var MAIL_RECEIPT_CUTOFF = timestamp('2026-12-01T00:00:00-05:00');

  function state(config, nowMs) {
    config = config || {};
    var rate = positiveNumber(config.entriesPerDollar);
    var now = nowMs === undefined ? Date.now() : nowMs;
    var campaignStart = timestamp(config.startsAt === undefined ? '2026-09-21T18:00:00-04:00' : config.startsAt);
    var campaignEnd = timestamp(config.endsAt === undefined ? '2026-11-20T23:59:59-05:00' : config.endsAt);
    var campaignValid = campaignStart !== null && campaignEnd !== null && campaignStart < campaignEnd &&
      typeof now === 'number' && Number.isFinite(now);
    var campaignState = !campaignValid ? 'invalid' : now < campaignStart ? 'upcoming' : now > campaignEnd ? 'ended' : 'live';
    var result = {
      enabled: config.enabled === true,
      rate: rate || 0,
      campaignState: campaignState,
      campaignActive: campaignState === 'live',
      campaignStartsAt: campaignStart,
      campaignEndsAt: campaignEnd,
      multiplier: 1,
      multiplierActive: false,
      multiplierConfigured: false,
      startsAt: null,
      endsAt: null,
      status: 'disabled',
      error: null
    };
    if (!result.enabled) return result;
    if (rate === null) {
      result.enabled = false;
      result.status = 'invalid';
      result.error = 'Entries per dollar must be a positive number, such as 1 or 0.2.';
      return result;
    }
    result.status = 'base';
    if (!campaignValid) {
      result.status = 'invalid';
      result.error = 'Set valid campaign dates with explicit time zones and verify the current time. Base estimates remain available.';
      return result;
    }
    if (config.multiplierEnabled !== true) return result;
    var factor = positiveNumber(config.multiplierValue);
    if (factor !== 2 && factor !== 3) {
      result.status = 'invalid';
      result.error = 'Only announced 2× or 3× bonus periods are permitted. Base entries apply.';
      return result;
    }
    var startText = typeof config.multiplierStartsAt === 'string' ? config.multiplierStartsAt.trim() : '';
    result.startsAt = timestamp(startText);
    result.endsAt = timestamp(config.multiplierEndsAt);
    if (result.endsAt === null || result.startsAt === null) {
      result.status = 'invalid';
      result.error = 'Set both bonus start and end dates with explicit time zones. Base entries apply.';
      return result;
    }
    if (result.startsAt >= result.endsAt) {
      result.status = 'invalid';
      result.error = 'The multiplier end must be later than its start. Base entries apply.';
      return result;
    }
    if (result.startsAt < campaignStart || result.endsAt > campaignEnd) {
      result.status = 'invalid';
      result.error = 'The complete bonus purchase window must fall within the campaign. Base entries apply.';
      return result;
    }
    var periodId = typeof config.multiplierPeriodId === 'string' ? config.multiplierPeriodId.trim() : '';
    var mailRate = positiveNumber(config.multiplierMailInRate);
    var postmarkStart = timestamp(config.multiplierPostmarkStartsAt);
    var postmarkEnd = timestamp(config.multiplierPostmarkEndsAt);
    var receiptDeadline = timestamp(config.multiplierReceiptDeadline);
    if (!periodId || config.multiplierAnnouncementPublished !== true || mailRate !== factor * 10 ||
        postmarkStart === null || postmarkEnd === null || receiptDeadline === null ||
        postmarkStart > postmarkEnd || receiptDeadline < postmarkEnd) {
      result.status = 'invalid';
      result.error = 'Complete the prior homepage/social announcement confirmation, Bonus Period ID, 20-entry/30-entry mail-in rate, and ordered postmark/receipt dates. Base entries apply.';
      return result;
    }
    if (receiptDeadline >= MAIL_RECEIPT_CUTOFF) {
      result.status = 'invalid';
      result.error = 'The mail-in receipt deadline must be before December 1, 2026 at 12:00 a.m. ET, with additional time for processing before the drawing. Base entries apply.';
      return result;
    }
    result.multiplierConfigured = true;
    if (now >= result.endsAt) {
      result.status = 'expired';
    } else if (now < result.startsAt) {
      result.status = 'scheduled';
    } else {
      result.status = 'active';
      result.multiplier = factor;
      result.multiplierActive = true;
    }
    return result;
  }

  function entries(cents, config, nowMs) {
    if (typeof cents === 'string' && /^\d+$/.test(cents.trim())) cents = Number(cents.trim());
    if (!Number.isSafeInteger(cents) || cents < 0) return 0;
    var current = state(config, nowMs);
    if (!current.enabled) return 0;
    // Final rules count whole eligible transaction dollars before applying a rate.
    var exact = Math.floor(cents / 100) * current.rate * current.multiplier;
    if (!Number.isFinite(exact) || exact > Number.MAX_SAFE_INTEGER) return 0;
    var nearest = Math.round(exact);
    var tolerance = Math.min(1e-7, Number.EPSILON * Math.max(1, Math.abs(exact)) * 4);
    return Math.floor(Math.abs(exact - nearest) <= tolerance ? nearest : exact);
  }

  function moneyInteger(value) {
    if (typeof value === 'string' && /^\d+$/.test(value.trim())) value = Number(value.trim());
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  function cartEligibleCents(cart) {
    if (!cart || typeof cart !== 'object') return null;
    // Shopify total_price already includes both line-level and cart-level discounts.
    // https://shopify.dev/docs/api/liquid/objects/cart#cart-total_price
    if (Object.prototype.hasOwnProperty.call(cart, 'total_price')) return moneyInteger(cart.total_price);
    if (!Array.isArray(cart.items)) return null;
    var total = 0;
    for (var index = 0; index < cart.items.length; index++) {
      var item = cart.items[index];
      if (!item || typeof item !== 'object') return null;
      var amount = moneyInteger(item.final_line_price);
      if (amount === null || !Number.isSafeInteger(total + amount)) return null;
      total += amount;
    }
    // final_line_price includes only line discounts; subtract cart allocations once.
    var discounts = cart.cart_level_discount_applications || [];
    if (!Array.isArray(discounts)) return null;
    for (var discountIndex = 0; discountIndex < discounts.length; discountIndex++) {
      var discount = discounts[discountIndex];
      if (!discount || typeof discount !== 'object') return null;
      if (discount.target_type === 'shipping_line') continue;
      var deduction = moneyInteger(discount.total_allocated_amount);
      if (deduction === null) return null;
      total -= deduction;
      if (!Number.isSafeInteger(total) || total < 0) return null;
    }
    return total;
  }

  function cartEntries(cart, config, nowMs) {
    var cents = cartEligibleCents(cart);
    return cents === null ? null : entries(cents, config, nowMs);
  }

  // This is an upper-bound storefront estimate, not a balance across prior entries.
  function estimate(cents, config, nowMs) {
    return Math.min(1000, entries(cents, config, nowMs));
  }

  function cartEstimate(cart, config, nowMs) {
    var raw = cartEntries(cart, config, nowMs);
    return raw === null ? null : Math.min(1000, raw);
  }

  return Object.freeze({ state: state, entries: entries, estimate: estimate,
    cartEligibleCents: cartEligibleCents, cartEntries: cartEntries, cartEstimate: cartEstimate });
});
